<template>
    <InputLayout :v="v" :label="label">
        <div v-if="errorMessage" class="text-danger text-center">
            {{ errorMessage }}
        </div>
        <div>
            <ImageUploadGuide v-if="isAligner" :imageUploadGuide="imageUploadGuide" @click="clickUploadZone" />
            <div
                class="file-upload-zone"
                :class="{ 'active': dropzoneIsActive, 'is-invalid': v && v.$error }"
                @dragenter.prevent="toggleUploadZoneActive"
                @dragleave.prevent="toggleUploadZoneActive"
                @dragover.prevent
                @drop.prevent="dropUploadZone($event)"
                @click="clickUploadZone"
            >
                <input type="file" ref="fileUpload" class="d-none" multiple @change="filesChange($event.target.files)" />
                <div>Drop files here to upload or <a href="#">click here</a> to choose a file</div>
                <div>Only {{ allowedExtensionsString() }} file types are supported.</div>
                <div v-if="uploadingFiles.length" class="file-upload-progress">
                    <div class="file-upload-progress-fill" :style="{ width: totalProgress + '%' }"></div>
                </div>
            </div>
            <span class="invalid-feedback" v-if="v && v.$error">{{v.$errors[0].$message}}</span>
        </div>
        <div class="form-text" v-for="(unprocessedFile, index) of unprocessedFiles" v-bind:key="index">
            {{unprocessedFile.file.name}}: {{unprocessedFile.message}}
        </div>
        <LightboxGallery v-if="previewFiles.length" :files="previewFiles" :allow-delete="allowDelete" class="mt-4" @remove="removeFile" />
    </InputLayout>
</template>

<style scoped lang="scss">
    .file-upload-zone
    {
        position: relative;
        text-align: center;
        border: $border-width dashed $card-border-color;
        @include border-radius($border-radius);
        padding: $spacer*2 $spacer;
        overflow: hidden;

        &.active
        {
            background: #EEEEEE;
        }

        &.is-invalid
        {
            border-color: $danger;
        }
    }

    .file-upload-progress
    {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 3px;
        background: rgba(0, 0, 0, 0.06);
    }

    .file-upload-progress-fill
    {
        height: 100%;
        background: $swift-fresh-green;
        transition: width 0.2s ease;
    }

    .from-qr-code {
        font-size: 0.9rem;
        margin: 0;
        padding: 0;
    }
</style>

<script>
    import ImageUploadGuide from '@/components/ImageUploadGuide.vue';
    import FileUpload from "@/services/FileUpload";
    import InputLayout from '@/components/InputLayout.vue';
    import LightboxGallery from '@/components/LightboxGallery.vue';
    
    export default {
        props: {
            modelValue: {
                required: true,
            },
            v: {
                required: true,
            },
            label: {
                required: true,
            },
            allowedExtensions: {
                required: true,
            },
            imageUploadGuide: {
                default: [],
            },
            isAligner: {
                default: false,
            },
            qrUplaodedFiles: {
                default: [],
                required: false,
            },
            url: {
                default: '/api/v1/files',
            },
            completeUrl: {
                default: '/api/v1/files/complete',
            },
            token: {
                default: '',
            },
            allowDelete: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            return {
                dropzoneIsActive: false,
                processingFiles: [],
                uploadedFiles: this.modelValue,
                unprocessedFiles: [],
                errorMessage: null,
            }
        },
        emits: ['update:modelValue'],
        computed: {
            allFiles () {
                var processingFiles = this.processingFiles.filter(processingFile => !processingFile.processed)
                var uploadedFiles = this.uploadedFiles;
                return uploadedFiles.concat(processingFiles);
            },
            uploadingFiles () {
                return this.processingFiles.filter(processingFile => !processingFile.processed);
            },
            totalProgress () {
                if (!this.uploadingFiles.length) return 0;
                let totalBytes = 0;
                let uploadedBytes = 0;
                this.uploadingFiles.forEach((pf) => {
                    if (!pf.file || !pf.uploadData) return;
                    totalBytes += pf.file.size;
                    uploadedBytes += pf.uploadData.parts
                        .map((part) => part.uploadedBytes)
                        .reduce((a, b) => a + b, 0);
                });
                return totalBytes ? (uploadedBytes / totalBytes) * 100 : 0;
            },
            previewFiles () {
                return this.uploadedFiles.map((pf) => ({
                    id: pf.hash || pf.id,
                    file_name: pf.name || pf.file_name,
                    url: this.getPreviewUrl(pf),
                    file_size: pf.file_size ?? pf.file?.size,
                }));
            },
        },
        components: {
            InputLayout,
            ImageUploadGuide,
            LightboxGallery,
        },
        created() {
            this.blobUrls = new Map();
        },
        mounted() {
            this.checkAllowedExtensions();
        },
        unmounted() {
            this.blobUrls.forEach((url) => URL.revokeObjectURL(url));
            this.blobUrls.clear();
        },
        methods: {
            toggleUploadZoneActive: function() {
                this.dropzoneIsActive = !this.dropzoneIsActive;
            },
            clickUploadZone: function () {
                this.$refs.fileUpload.click();
            },
            dropUploadZone: function (event) {
                this.toggleUploadZoneActive();
                this.filesChange(event.dataTransfer.files)
            },
            removeFile: function(galleryFile) {
                const target = this.uploadedFiles.find((pf, i) => {
                    const id = pf.hash || `${pf.name || pf.file_name}-${i}`;
                    return id === galleryFile.id;
                });
                if (!target) return;

                const key = target.hash || target.name;
                const blobUrl = this.blobUrls.get(key);
                if (blobUrl) {
                    URL.revokeObjectURL(blobUrl);
                    this.blobUrls.delete(key);
                }

                this.uploadedFiles = this.uploadedFiles.filter(pf => pf !== target);
                this.processingFiles = this.processingFiles.filter(pf => pf !== target);
                this.$emit('update:modelValue', this.uploadedFiles);
            },
            getPreviewUrl: function(pf) {
                if (pf.url) return pf.url;
                if (!pf.file) return '';
                const key = pf.hash || pf.name;
                if (!this.blobUrls.has(key)) {
                    this.blobUrls.set(key, URL.createObjectURL(pf.file));
                }
                return this.blobUrls.get(key);
            },
            calculateUploaded: function(processingFile, forBar) {
              if(!forBar) {
                if (processingFile.processed) {
                  return '<span><i class="bi-check2 navbar-icon"></i></span>';
                }

              } else {
                if (processingFile.processed) {
                  return '100%';
                }
              }

              var  uploadedBytes =  processingFile.uploadData.parts.map(part => part.uploadedBytes).reduce((partialSum, a) => partialSum + a, 0);
              return Math.ceil((uploadedBytes / processingFile.file.size) * 100) + '%';

            },
            allowedExtensionsString:  function() {
                return this.allowedExtensions
                .map(extension => '.' + extension)
                .join(", ")
                .replace(/, ((?:.(?!, ))+)$/, ' and $1');
            },
            checkAllowedExtensions: function() {
                var missingExtensions = this.allowedExtensions.filter((extension) =>  {
                    return FileUpload.getMimetype(extension) === null;
                });

                if(missingExtensions.length)
                {
                    alert('Missing mimetype for ' + missingExtensions);
                }
            },
            filesChange: function (fileList) {
                if (!fileList.length) {
                    return;
                }

                this.unprocessedFiles = [];
                
                Array
                .from(Array(fileList.length).keys())
                .forEach(x => {
                    this.processFile(fileList[x], this.url, this.token);
                });
            },
            processFile: function (file, url = '/api/v1/files', token='') {
                var fileExtension = FileUpload.getExtension(file);
                var mimetype = FileUpload.getMimetype(fileExtension);

                if(fileExtension === null || !this.allowedExtensions.includes(fileExtension))
                {
                    this.unprocessedFiles.push({
                        file: file,
                        message: 'filetype not allowed',
                    });

                    return false;
                }

                FileUpload.startUpload(file.name, file, mimetype, url, token)
                .then((uploadData) => {
                    var processingFile = this.getNewProcessingFile(file, uploadData);

                    var fileIndex = this.processingFiles.push(processingFile) - 1;

                    this.upload(processingFile, fileIndex);
                })
                .catch((error) => {
                    if (error.response.status == 401) {
                        this.errorMessage = error.response.data.message;
                        return;
                    }
                    this.unprocessedFiles.push({
                        file: file,
                        message: typeof error.response.data.message !== 'undefined' ? error.response.data.message : null,
                    });
                });
            },
            getNewProcessingFile: function(file, uploadData) {
                return {
                    name: file.name,
                    hash: null,
                    processed: false,
                    file: file,
                    uploadData: uploadData,
                };
            },
            upload: function (processingFile, fileIndex) {
                FileUpload.uploadParts(this.processingFiles[fileIndex].uploadData) // Must remain as this.processingFiles. Using processingFile instead breaks onUploadProgress
                .then(() => {
                    this.completeUpload(processingFile, this.completeUrl, this.token);
                })
                .catch(() => {

                });
            },
            completeUpload: function(processingFile, url = '/api/v1/files/complete', token='')
            {
                FileUpload.completeUpload(processingFile.uploadData, url, token)
                .then((fileHash) => {
                    processingFile.hash = fileHash;
                    processingFile.processed = true;

                    this.uploadedFiles.push(processingFile);
                    this.$emit('update:modelValue', this.uploadedFiles);
                })
                .catch(() => {
                    
                });
            },
        },
        watch: {
            modelValue: function(val) {
                this.uploadedFiles = val;
            }
        },
    }
</script>
